import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';

import { Observable, from, of, BehaviorSubject, EMPTY, Subject, timer, firstValueFrom } from 'rxjs';
import { tap, map, filter, startWith, switchMap, catchError, throttleTime, retry, take } from 'rxjs/operators';

import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import {
   EnvironmentService,
   ErrorMessageService,
   MaintenanceService,
   ResetService,
} from '@library/core/services';
import { ThemeService } from '@library/common/branding';
import { HttpCancelService } from '@library/common/http';
import { UserService } from '@library/modules/user';

import { Auth, CognitoUser } from '@aws-amplify/auth';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

export enum AuthStates {
   SignedOut = 'signedout',
   SignedIn = 'signedin',
   VerifyEmail = 'verifyemail',
   ForgotPassword = 'forgotpassword',
   ResetPassword = 'resetpassword',
   NewPassword = 'newpassword',
}

@Injectable({
   providedIn: 'root',
})
export class AuthService {
   lastUrl = '/';
   locked = false;
   working = false;

   authState$: BehaviorSubject<string> = new BehaviorSubject( AuthStates.SignedOut );

   CognitoUser: CognitoUser;
   activeEmail: string;

   authSnackbar: MatSnackBarRef<any>;

   _loggedIn = false;
   private _initialLogin = false;
   private _visibility$ = new Subject<void>();
   private _refreshTimer: any;
   /**
    * Access token expiry (epoch seconds) observed the last time a refresh was
    * scheduled. Used by scheduleTokenRefresh to detect whether Auth.currentSession()
    * actually rotated the token or just handed back the same cached session.
    */
   private _lastScheduledExp: number | null = null;
   /**
    * Consecutive count of refresh cycles where the token did NOT advance. Drives
    * exponential backoff so a stuck refresh doesn't spin at a fixed 30 s pace
    * indefinitely. Reset to 0 the moment the token genuinely rotates.
    */
   private _staleRefreshAttempts = 0;
   /**
    * Cached session promise to prevent multiple concurrent Auth.currentSession() calls.
    * This ensures all simultaneous requests share the same promise, reducing AWS API calls
    * and preventing race conditions during token refresh.
    */
   private _sessionPromise: Promise<any> | null = null;
   private _sessionClearTimer: any = null;
   /**
    * Guard flag that is true while a proactive token refresh is in flight.
    * Other parts of the service (visibility handler, setAuthHeaders) must NOT
    * trigger a logout while this flag is true because the session is
    * temporarily invalid during the Cognito refresh exchange.
    */
   private _refreshing = false;
   private _refreshScheduled = false;
   /**
    * Incremented by clearAuthState() on every logout/lock.
    *
    * Session lookups started before a sign-out can resolve after it, and would
    * otherwise re-arm the refresh timer or repopulate the session cache with
    * tokens the user just signed out of. Those callbacks capture the epoch
    * they started with and bail out if it no longer matches. A counter is used
    * rather than _loggedIn because _loggedIn also tracks session validity, so
    * it can be false for reasons that are not a sign-out.
    */
   private _authEpoch = 0;

   /**
    * Observable stream of authentication status.
    * Uses optimized session caching to avoid repeated Auth.currentSession() calls.
    * Returns true if user has valid session, false otherwise.
    */
   get isLoggedIn$(): Observable<boolean> {
      return this.session.pipe(
         map( session => session?.isValid() || false ),
         map( _ => this.e.environment?.mock ? true : _ ),
         /**
          * Only ever raise the flag here, never lower it. clearAuthState() is
          * the sole writer that lowers it, on logout/lock.
          *
          * This stream resolves asynchronously, so a lookup started before a
          * sign-in can resolve after it. Lowering the flag from here would
          * then undo what handleSignedInState() just set, which disables the
          * wait-for-a-fresh-session branch in setAuthHeaders and lets a
          * request go out with no Authorization header at all.
          */
         tap( loggedIn => this._loggedIn = this._loggedIn || loggedIn ),
      );
   }

   /**
    * Cached session observable with promise deduplication and short TTL.
    * Prevents multiple concurrent Auth.currentSession() calls which can cause
    * race conditions during token refresh. Retries up to 3 times on failure.
    * A resolved session is held for 5 s so near-simultaneous callers share the
    * same freshly-resolved session; a null one is not cached at all.
    */
   get session(): Observable<CognitoUserSession> {
      if ( !this._sessionPromise ) {
         const session$ = from( Auth.currentSession() ).pipe(
            retry({
               count: 2,
               delay: ( error, retryCount ) => {
                  console.warn( `Auth.currentSession() attempt ${retryCount} failed, retrying...`, error );
                  return timer( 100 * retryCount ); // 100ms, 200ms backoff
               },
            }),
            catchError( error => {
               console.warn( 'Auth.currentSession() failed after 3 attempts', error );
               return of( null );
            }),
         );
         /**
          * A resolved session is held for 5 s so overlapping callers share it.
          * A null one is dropped immediately.
          *
          * Caching the null was the more damaging half: on the login page the
          * bootstrap request (maintenance check) triggers a session lookup
          * that fails because nobody is signed in yet, and the null result
          * then stood in as "the session" for the next 5 s. A login completed
          * inside that window handed the first /user request a cached null
          * and the request went out unauthenticated.
          *
          * The identity check keeps a late resolution from clearing a newer
          * promise — scheduleTokenRefresh() publishes its in-flight rotation
          * into _sessionPromise directly.
          */
         const pending: Promise<any> = firstValueFrom( session$ ).then( session => {
            if ( this._sessionPromise === pending ) {
               session ? this.scheduleSessionCacheClear() : this.clearSessionCache();
            }

            return session;
         });

         this._sessionPromise = pending;
      }
      return from( this._sessionPromise );
   }

   get currentUser(): Observable<CognitoUser> {
      return from( Auth.currentUserPoolUser({ bypassCache: true }).catch( _ => null ));
   }

   constructor(
      private router: Router,
      private theme: ThemeService,
      private e: EnvironmentService,
      private error: ErrorMessageService,
      private user: UserService,
      private reset: ResetService,
      private snackbar: MatSnackBar,
      private maintenance: MaintenanceService,
      private cancelService: HttpCancelService,
   ) {
      // listen for branding changes
      this.theme.themeChange$.pipe(
         startWith( this.theme.currentTheme ),
      ).subscribe( theme =>
         Auth.configure({
            authenticationFlowType: 'USER_PASSWORD_AUTH',
            clientMetadata: {
               portal: document.title,
               brand: theme.class,
               url: document.location.origin,
            }
         })
      );

      // on load match the session state
      this.isLoggedIn$.pipe(
         filter( signedin => !!signedin ),
         switchMap( _ => this.currentUser ),
      ).subscribe( cognitoUser => this.handleSignedInState( cognitoUser ));

      // set last url
      this.router.events.pipe(
         filter( event => event instanceof NavigationEnd ),
         filter(( event: NavigationEnd ) => ![ '/login', '/logout', '/lock' ].includes( event.url )),
      ).subscribe( _ => this.lastUrl = this.router.url );

      /**
       * Tab visibility change handler for proactive token refresh.
       * 
       * When user returns to the tab after being away, we check if the session
       * is still valid. Auth.currentSession() will silently refresh the access token
       * using the refresh token if needed. This prevents "User session not found" errors
       * that occur when the access token expires but the frontend doesn't know.
       * 
       * Throttled to 10 seconds to avoid excessive API calls during rapid tab switching.
       */
      document.addEventListener( 'visibilitychange', () => {
         if ( document.visibilityState === 'visible' && this._loggedIn ) {
            this._visibility$.next();
         }
      });

      this._visibility$.pipe(
         throttleTime( 10_000 ),
         // Skip session check while a proactive refresh is already in flight.
         // The refresh callback will reschedule once it finishes.
         filter( () => !this._refreshing ),
         switchMap( () => {
            // Force a fresh session check when returning from idle so
            // Cognito can silently refresh the access token if needed.
            this.clearSessionCache();
            const epoch = this._authEpoch;
            return this.session.pipe( map( session => ({ session, epoch }) ));
         }),
      ).subscribe( ({ session, epoch }) => {
         // A logout/lock landed while the session check was in flight.
         if ( this._authEpoch !== epoch ) { return; }

         if ( !session || !session.isValid() ) {
            // Session expired during tab idle - logout gracefully
            this.logout().catch( () => {} );
         } else {
            // Session still valid - schedule proactive refresh
            this.scheduleTokenRefresh();
         }
      });
   }

   handleSignedInState( user: CognitoUser = null ) {
      if ( !user ) { return; }

      this.CognitoUser = user;
      if (( user as any )?.challengeName === 'NEW_PASSWORD_REQUIRED' ) {
         // create a new password
         this.authState$.next( AuthStates.NewPassword );
         this.activeEmail = ( user as any )?.challengeParam?.userAttributes?.email;
         this.working = false;
      } else if ( !(user as any).attributes?.email_verified ) {
         this.authState$.next( AuthStates.VerifyEmail );
         this.sendVerificationEmail();
         this.working = false;
      } else {
         this.authState$.next( AuthStates.SignedIn );

         /**
          * Mark the user as signed in here rather than waiting for the next
          * isLoggedIn$ emission.
          *
          * isLoggedIn$ is the only other writer of this flag and its sole
          * subscriber is the constructor, which runs once at startup. After a
          * fresh login nothing re-evaluates it, so _loggedIn stayed false until
          * a page reload. That left setAuthHeaders unable to take its
          * wait-for-a-fresh-session branch — an invalid or mid-rotation session
          * fell straight through and the request went out with no Authorization
          * header at all. It also left the visibility handler inert.
          *
          * clearAuthState() sets it back to false on logout/lock.
          */
         this._loggedIn = true;

         this.user.user.pipe(
            /**
             * Retry logic for initial user data loading.
             * 
             * After login, we need to load user data from the backend. The backend
             * requires the ID token for user identification on first login. We retry
             * up to 2 times with 2s delays because:
             * 1. Network issues might cause the first request to fail
             * 2. The _initialLogin flag stays true, ensuring ID token is included in retries
             * 3. This prevents immediate logout after successful login due to transient issues
             */
            retry({
               count: 2,
               delay: () => timer( 2000 ), // 2 s delay between retries
            }),
            catchError( error => {
               /**
                * The retries are exhausted: Cognito accepted the credentials but
                * the first backend call (GET user) never succeeded, so the user
                * is authenticated without any usable account data.
                *
                * Log out unconditionally. The sign-in produced real access, ID
                * and refresh tokens — leaving them in place would strand the
                * user in a half-signed-in state that still carries valid
                * credentials. logout() tears down every token reference and
                * returns to the login page.
                *
                * This previously only happened for unstructured errors; a
                * backend error carrying `messages` just showed a snackbar and
                * kept the session alive.
                */
               // Safely access nested error messages (may not exist for network/timeout errors)
               const messages = error?.error?.messages;

               // VERSION_EXPIRED is already surfaced by the version-expired dialog the HTTP
               // interceptor opens for this same failed request (which reloads the page on
               // close) — skip the generic logout + snackbar below so the two don't fight
               // over the user.
               if ( messages?.some(( m: { code?: string } ) => m.code === 'VERSION_EXPIRED' )) {
                  return EMPTY;
               }

               this.logout().catch( () => {} );

               if ( messages?.length ) {
                  console.log( 'sign in error', messages );

                  // Opened after logout() so the reason survives the redirect
                  // back to the login page.
                  this.authSnackbar = this.snackbar.open( messages[0].message, 'Close' );
                  this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );

                  return EMPTY;
               }

               console.error( 'Failed to load user data after sign in', error );

               setTimeout(() => this.error.push( 'This portal is not currently working, please contact your administrator' ), 100 );

               return EMPTY;
            }),
         ).subscribe( _ => {
            this._initialLogin = false;
            this.scheduleTokenRefresh();
            if ( [ '/login', '/logout', '/lock' ].includes( this.router.url )) {
               this.router.navigate([ '/' ]);
            }
         });
      }
   }

   /**
    * Sets Authorization header for API requests.
    * 
    * Key improvements:
    * 1. Uses cached session to avoid repeated Auth.currentSession() calls
    * 2. Includes ID token on initial login for backend user identification
    * 3. Prevents overwriting existing Authorization headers
    * 4. Graceful error handling for corrupted session data
    * 
    * The ID token is appended (comma-separated) only on initial login because:
    * - Backend needs it to identify the user and create session record
    * - After initial login, access token alone is sufficient for authentication
    * - The _initialLogin flag ensures retries also include ID token
    */
   setAuthHeaders( headers: HttpHeaders ): Observable<HttpHeaders> {
      if ( !headers ) { headers = new HttpHeaders(); }

      return this.session.pipe(
         switchMap( session => {
            // If session is invalid but user is supposed to be logged in,
            // clear the cached (possibly stale/null) promise and retry once
            // so the request waits for a fresh Auth.currentSession() call.
            // When a proactive refresh is in flight, wait for it to finish
            // instead of clearing the cache (which would start a competing call).
            if ( ( !session || !session.isValid() ) && this._loggedIn ) {
               if ( !this._refreshing ) { this.clearSessionCache(); }
               return this.session;
            }
            return of( session );
         }),
         map( session => {
            try {
               // Skip if no valid session
               if ( !session || !session.isValid() ) { return headers; }

               // Skip if Authorization header is already set
               if ( headers.get('Authorization') ) { return headers; }

               // Build the Bearer token with the access token
               let authHeaders = `Bearer ${session.getAccessToken().getJwtToken()}`;

               // On initial login, append the ID token ('.'-joined) for backend user identification.
               // The flag must stay true until the user-data load actually succeeds so that
               // retries AND any concurrent post-login requests also carry the ID token.
               // (handleSignedInState clears _initialLogin on success or after exhausting retries.)
               // Previously it was cleared here, the moment the FIRST header was built, so a
               // failed/retried first request — or sibling requests fired in the same tick —
               // went out with only the access token and hit the empty-email backend path.
               if ( this._initialLogin ) {
                  authHeaders += '.' + session.getIdToken().getJwtToken();
               }

               headers = headers.set( 'Authorization', authHeaders );

               return headers;
            } catch ( e ) {
               // Return fresh headers to avoid propagating a corrupted HttpHeaders object
               return new HttpHeaders();
            }
         }),
      );
   }

   login( username: string, password: string ): Observable<CognitoUser> {
      this.working = true;

      return from( Auth.signIn( username, password )).pipe(
         /**
          * Drop whatever the pre-login session lookups left behind before any
          * post-login request can read it. Sign-in has just written a brand
          * new token set, so anything cached from the login page describes a
          * user who no longer exists.
          */
         tap( _ => this.clearSessionCache() ),
         tap( _ => this._initialLogin = true ),
         // tap( response => console.log( 'response', response )),
         tap( cognitoUser => this.handleSignedInState( cognitoUser )),
         tap( _ => this.maintenance.check() ),
         // dismiss snackbar when login success
         tap( _ => this.authSnackbar ? this.authSnackbar.dismiss() : null ),
         catchError( error => {
            if ( error.message ) {
               // auto dismiss snackbar within 15secs when there's an error.
               this.authSnackbar = this.snackbar.open( error.message, 'Close', { duration: 15000 } );
               this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
               this.working = false;
            }

            throw error;
         }),
      );
   }

   /**
    * Logs out the user and cleans up all authentication state.
    *
    * Improvement: Uses localStorage.clear() instead of selective cleanup
    * to ensure all cached data is removed, preventing stale data issues
    * on next login.
    *
    * The in-memory token state is cleared twice: once up front so nothing
    * new can be scheduled, and again in the finally block AFTER
    * Auth.signOut() / localStorage.clear(). The second pass matters because
    * Auth.signOut() is awaited — any caller hitting the `session` getter
    * during that await would otherwise repopulate the cached session promise
    * with a still-valid token set that outlives the logout.
    */
   async logout() {
      this.working = true;
      this.clearRefreshTimer();
      this.clearSessionCache();
      this.cancelService.cancelPendingRequests(); // Cancel pending HTTP requests
      this.clearAuthState();

      try {
         await Auth.signOut();
      } catch ( e ) {
         console.error( 'Auth.signOut() failed', e );
      } finally {
         this.authState$.next( AuthStates.SignedOut );
         localStorage.clear(); // Clear all cached data to prevent stale state
         this.clearAuthState();
         this.user.destroy();
         this.reset.reset();
         this.router.navigate([ 'login' ]);
         this.maintenance.check();
         this.working = false;
      }
   }

   /**
    * Locks the session. Same token teardown as logout() but keeps lastUrl and
    * localStorage intact so the user returns to where they left off.
    */
   async lock( url: string = this.router.url ) {
      this.working = true;
      this.lastUrl = url === '/lock' ? '/' : url;
      this.clearAuthState();

      try {
         await Auth.signOut();
      } catch ( e ) {
         console.error( 'Auth.signOut() failed', e );
      } finally {
         this.authState$.next( AuthStates.SignedOut );
         this.clearAuthState();
         this.router.navigate([ 'login' ]);
         this.maintenance.check();
         this.working = false;
      }
   }

   sendVerificationEmail() {
      this.working = true;
      this.activeEmail = ( this.CognitoUser as any ).attributes.email;

      Auth.verifyUserAttribute( this.CognitoUser, 'email' ).then( _ => this.working = false );
   }

   verifyEmail( code: string ) {
      this.working = true;

      return from( Auth.verifyUserAttributeSubmit( this.CognitoUser, 'email', code )).pipe(
         switchMap( _ => Auth.currentUserPoolUser({ bypassCache: true })),
         tap( user => this.handleSignedInState( user )),
         tap( _ => this.activeEmail = null ),
         tap( _ => this.working = false ),
      );
   }

   sendForgotPasswordEmail( email: string ) {
      this.working = true;
      this.activeEmail = email;

      Auth.forgotPassword( email ).then( _ => {
         this.working = false;
         this.authState$.next( AuthStates.ResetPassword );
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      });
   }

   resetPassword( code: string, password: string ) {
      this.working = true;

      return Auth.forgotPasswordSubmit( this.activeEmail, code.trim(), password ).then( _ => {
         this.login( this.activeEmail, password ).subscribe();
         this.activeEmail = null;
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      });
   }

   /**
    * Schedules proactive token refresh 1 minute before expiry.
    *
    * This prevents "User session not found" errors by ensuring the access
    * token is always fresh. The refresh happens silently in the background.
    *
    * Key benefits:
    * 1. Prevents session expiration during user activity
    * 2. Reduces failed API calls due to expired tokens
    * 3. Provides seamless user experience without interruption
    * 4. Automatically reschedules after successful refresh
    *
    * Uses take(1) to prevent stacked subscriptions — each call creates
    * exactly one subscription that auto-completes after emitting.
    * The _refreshing flag prevents the visibility handler and auth header
    * logic from triggering a logout while the Cognito refresh is in flight.
    *
    * When the timer fires while the document is hidden (PC locked / tab
    * background), the network may be unavailable, causing Auth.currentSession()
    * to fail. In that case we skip the logout and let the visibility handler
    * retry with a live network when the user returns.
    *
    * Because Auth.currentSession() only rotates the token once it's actually
    * expired (not proactively), a refresh cycle that fires early will get back
    * the same session it started with. This is detected via _lastScheduledExp /
    * _staleRefreshAttempts and handled with exponential backoff instead of
    * looping at a fixed 30 s interval indefinitely.
    */
   private scheduleTokenRefresh() {
      if ( this._refreshScheduled ) {
         console.log( '[AuthService] scheduleTokenRefresh: skipped — already scheduled at', new Date().toISOString() );
         return;
      }

      this.clearRefreshTimer();
      this._refreshScheduled = true;

      const scheduleEpoch = this._authEpoch;

      this.session.pipe( take( 1 ) ).subscribe( session => {
         // A logout/lock landed while this session lookup was in flight —
         // don't arm a refresh timer for a session that no longer exists.
         if ( this._authEpoch !== scheduleEpoch ) { return; }

         if ( !session || !session.isValid() ) {
            this._refreshScheduled = false;
            return;
         }

         const exp = session.getAccessToken().getExpiration(); // epoch seconds
         const now = Math.floor( Date.now() / 1000 );

         // Auth.currentSession() only rotates the access token once it has actually
         // expired — calling it proactively (as the refresh cycle below does, ~1 min
         // before expiry) just hands back the SAME cached session. If we don't detect
         // that, `exp` never advances, `rawMs` goes negative forever, and the old fixed
         // 30 s floor turned this into a permanent tight loop (confirmed in production
         // session replays: refresh "succeeded" every 30 s for 40+ minutes without the
         // token ever actually rotating). Track the exp we last scheduled against and
         // only trust the fast path when the token has genuinely moved forward.
         const tokenAdvanced = this._lastScheduledExp === null || exp > this._lastScheduledExp;
         this._lastScheduledExp = exp;

         let refreshInMs: number;

         if ( tokenAdvanced ) {
            this._staleRefreshAttempts = 0;
            const rawMs = ( exp - now - 60 ) * 1000; // 1 min before expiry
            // Floor at 30 s as a sane minimum, not as the steady-state interval.
            refreshInMs = Math.max( rawMs, 30_000 );
         } else {
            // Token didn't advance — back off exponentially (30s, 60s, 120s, ...)
            // capped at 5 minutes, instead of hammering Auth.currentSession() every
            // 30 seconds indefinitely. This still recovers on its own once the token
            // actually expires and a real refresh happens.
            this._staleRefreshAttempts++;
            const backoffSeconds = Math.min( 30 * ( 2 ** this._staleRefreshAttempts ), 300 );
            refreshInMs = backoffSeconds * 1000;

            console.warn(
               '[AuthService] scheduleTokenRefresh: access token did not advance after refresh' +
               ' (stale attempt', this._staleRefreshAttempts + ') — backing off to', backoffSeconds,
               'seconds at', new Date().toISOString(),
            );
         }

         console.log( '[AuthService] scheduleTokenRefresh: scheduled in', Math.round( refreshInMs / 1000 ), 'seconds at', new Date().toISOString() );

         this._refreshTimer = setTimeout( () => {
            console.log( '[AuthService] scheduleTokenRefresh: token refresh fired at', new Date().toISOString() );
            this._refreshScheduled = false;

            // Signal that a refresh is in flight so other parts of the
            // service (visibility handler, setAuthHeaders) don't mistake
            // the temporarily-invalid session for a real sign-out.
            this._refreshing = true;

            // Drop the cached session, then force a real token rotation.
            this.clearSessionCache();

            const refreshEpoch = this._authEpoch;

            const rotation = this.forceRefreshSession()
               .catch( error => {
                  console.warn( '[AuthService] scheduleTokenRefresh: refresh failed', error );
                  return null as CognitoUserSession;
               })
               // Only if no logout/lock landed while the exchange was running,
               // otherwise this would arm a clear against a newer session.
               // A failed rotation resolves to null, and a null is dropped
               // rather than cached — same rule the session getter follows.
               .then( refreshed => {
                  if ( this._authEpoch === refreshEpoch && this._sessionPromise === rotation ) {
                     refreshed ? this.scheduleSessionCacheClear() : this.clearSessionCache();
                  }

                  return refreshed;
               });

            // Publish the in-flight rotation as the cached session so anything
            // asking for a session mid-exchange waits for the new token rather
            // than racing a second Cognito call and getting the outgoing one.
            this._sessionPromise = rotation;

            from( rotation ).pipe( take( 1 ) ).subscribe( refreshed => {
               this._refreshing = false;

               // This subscription is untracked, so it can resolve after a
               // logout/lock has already torn down the auth state. Bail out
               // instead of re-arming the refresh timer and repopulating the
               // session cache with tokens the user just signed out of.
               if ( this._authEpoch !== refreshEpoch ) { return; }

               if ( refreshed?.isValid() ) {
                  console.log( '[AuthService] scheduleTokenRefresh: refresh succeeded at', new Date().toISOString() );
                  // Refresh successful — schedule next refresh
                  this.scheduleTokenRefresh();
               } else if ( document.visibilityState === 'visible' ) {
                  // Refresh failed while user is actively using the app —
                  // refresh token is likely expired, logout gracefully.
                  this.logout().catch( () => {} );
               }
               // If the document is hidden (PC locked, tab in background),
               // do NOT logout. The network was likely unavailable during
               // sleep. The visibility handler will retry with a live
               // network when the user returns to the tab.
            });
         }, refreshInMs );
      });
   }

   private clearRefreshTimer() {
      if ( this._refreshTimer ) {
         clearTimeout( this._refreshTimer );
         this._refreshTimer = null;
      }
      this._refreshScheduled = false;
      // Reset stale-refresh tracking so the next login/lock cycle starts clean
      // rather than carrying over backoff state from a previous session.
      this._lastScheduledExp = null;
      this._staleRefreshAttempts = 0;
   }

   /**
    * Holds the resolved session promise for 5 s so overlapping callers reuse it.
    */
   private scheduleSessionCacheClear() {
      if ( this._sessionClearTimer ) { clearTimeout( this._sessionClearTimer ); }
      this._sessionClearTimer = setTimeout( () => {
         this._sessionPromise = null;
         this._sessionClearTimer = null;
      }, 5000 );
   }

   /**
    * Forces Cognito to actually rotate the access and ID tokens.
    *
    * Auth.currentSession() cannot do this. It ends up in CognitoUser.getSession(),
    * which returns the cached session untouched whenever session.isValid() is
    * true — and isValid() is purely expiry-based. Because the proactive refresh
    * runs a minute BEFORE expiry, the token is still valid at that point, so
    * currentSession() short-circuits and hands back the same token without ever
    * contacting Cognito. The refresh then reschedules against an unchanged
    * expiry and just spins until the token really expires.
    *
    * Going through refreshSession() with the refresh token issues a real
    * REFRESH_TOKEN_AUTH exchange and caches the new tokens, so the rotation
    * happens before expiry rather than after it.
    */
   private async forceRefreshSession(): Promise<CognitoUserSession> {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();

      return new Promise<CognitoUserSession>( ( resolve, reject ) => {
         // refreshSession() is not on the published CognitoUser typings.
         ( user as any ).refreshSession(
            session.getRefreshToken(),
            ( error: any, refreshed: CognitoUserSession ) => error ? reject( error ) : resolve( refreshed ),
         );
      });
   }

   private clearSessionCache() {
      this._sessionPromise = null;
      if ( this._sessionClearTimer ) {
         clearTimeout( this._sessionClearTimer );
         this._sessionClearTimer = null;
      }
   }

   /**
    * Drops every in-memory reference to the signed-in user's tokens.
    *
    * Covers:
    * - the cached session promise, which holds the access, ID and refresh
    *   tokens returned by Auth.currentSession()
    * - CognitoUser, whose signInUserSession holds the same three tokens
    * - the pending refresh timer and its scheduling/in-flight flags
    * - _loggedIn, which gates the setAuthHeaders retry path and the
    *   visibility handler; leaving it true lets both keep asking Cognito
    *   for tokens after sign-out
    */
   private clearAuthState() {
      this.clearRefreshTimer();
      this.clearSessionCache();

      this.CognitoUser = null;
      this.activeEmail = null;
      this._loggedIn = false;
      this._initialLogin = false;
      this._refreshing = false;

      // Invalidate any session lookup that is still in flight.
      this._authEpoch++;
   }

   newPassword( password: string ) {
      this.working = true;

      const currentUser = { ...this.CognitoUser };
      console.log( 'currentUser', currentUser, this.CognitoUser );

      return Auth.completeNewPassword( this.CognitoUser, password ).then( _ => {
         this.login( this.activeEmail, password ).subscribe();
         this.activeEmail = null;
      }, error => {
         this.authSnackbar = this.snackbar.open( error.message ?? 'Unknown Auth Error please contact your administrator', 'Close' );
         this.authSnackbar.onAction().subscribe( _ => this.authSnackbar.dismiss() );
         this.working = false;
      })
   }

   
}
